import { useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAppProperties, useCustomScripts } from "@/hooks";
import { useUser } from "@/providers/context";
import { getUrlFromFilename } from "@/utils";
import { ProtectedRoute } from "@/components";
import { CODE_VERSION } from "@/env.ts";

const FORCE_SIGN_IN_ALLOWED_PATHS = ["/sign-in", "/reset-password"];

export const Root = () => {
  const { data } = useAppProperties();
  const { pathname } = useLocation();
  const { uid } = useUser();

  const { data: headEndScripts } = useCustomScripts({
    location: "HeadEnd",
  });
  const { data: bodyEndScripts } = useCustomScripts({
    location: "BodyEnd",
  });

  useEffect(() => {
    if (headEndScripts) {
      headEndScripts.forEach((script) => {
        document.head.appendChild(script);
      });
    }
  }, [headEndScripts]);

  useEffect(() => {
    if (bodyEndScripts) {
      bodyEndScripts.forEach((script) => {
        document.body.appendChild(script);
      });
    }
  }, [bodyEndScripts]);

  const shouldForceSignIn = useMemo(
    () =>
      data?.WebForceSignIn === "1" &&
      !uid &&
      !FORCE_SIGN_IN_ALLOWED_PATHS.includes(pathname),
    [data?.WebForceSignIn, uid, pathname],
  );

  return (
    <>
      <Helmet>
        <title>{data?.WebTitle || data?.AppleTitle}</title>
        <link
          rel="icon"
          href={getUrlFromFilename(
            data?.AppIconWeb || data?.AppIconImage || "",
          )}
          type="image/png"
        />
        <meta name="version" content={CODE_VERSION} />
      </Helmet>
      {shouldForceSignIn ? (
        <ProtectedRoute>
          <Outlet />
        </ProtectedRoute>
      ) : (
        <Outlet />
      )}
    </>
  );
};
